import { useEffect } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Skeleton } from "@tellescope/ui";
import { useEnduserLogout } from "../store/hooks";
import { usePortalSchemaStore, usePortalSchema, usePortalSchemaLoading } from "../../shared/store/portalSchemaStore";
import { useOrganization } from "../../shared/store/organizationStore";
import { EnduserQueryProvider } from "../components/EnduserQueryProvider";
import { usePageRouting } from "../hooks/usePageRouting";
import { useSidebarEnabled } from "../hooks/useSidebarEnabled";
import { useAutoPageViewTracking } from "../hooks/useAutoPageViewTracking";
import { useLegacyRouteResolver } from "../hooks/useLegacyRouteResolver";
import { LegacyPageRenderer } from "../components/LegacyPageRenderer";
import { PortalPage } from "./PortalPage";

export const EnduserHomePage: React.FC = () => {
  const { pageSlug } = useParams<{ pageSlug?: string }>();
  const logout = useEnduserLogout();
  const organization = useOrganization();
  const schema = usePortalSchema();
  const isLoading = usePortalSchemaLoading();
  const fetchSchema = usePortalSchemaStore((state) => state.fetchSchema);
  const showSidebar = useSidebarEnabled();

  // URL-based page routing
  const { currentPage, navigateToPage } = usePageRouting(schema);

  // Legacy route resolution - determines if slug matches schema or legacy route
  const { isLegacyRoute, legacySlug, isValidRoute } = useLegacyRouteResolver(schema, pageSlug);

  useAutoPageViewTracking();

  // Fetch portal schema when organization is available
  useEffect(() => {
    // Wait for organization to load before fetching schema
    // This ensures we have the portalV2SchemaURL if it exists
    if (organization) {
      fetchSchema(organization.portalV2SchemaURL);
    }
  }, [organization, fetchSchema]);

  // Handle invalid routes - redirect to home
  if (!isLoading && schema && !isValidRoute) {
    return <Navigate to="/" replace />;
  }

  // Loading state - show while organization or schema is loading
  if (!organization || isLoading || !schema) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        {/* Skeleton navbar */}
        <div className="border-b bg-background">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <Skeleton className="h-6 w-32" />
            <div className="flex gap-4">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-16" />
            </div>
          </div>
        </div>

        {/* Skeleton content */}
        <div className="flex-1 p-6">
          <div className="max-w-4xl mx-auto space-y-4">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        </div>
      </div>
    );
  }

  // If it's a legacy route (not in schema), render the legacy page component
  if (isLegacyRoute && legacySlug) {
    return <LegacyPageRenderer slug={legacySlug} />;
  }

  // Render the portal schema page
  return (
    <EnduserQueryProvider>
      <PortalPage
        schema={schema}
        currentPage={currentPage}
        navigateToPage={navigateToPage}
        logout={logout}
        organization={organization}
        showSidebar={showSidebar}
      />
    </EnduserQueryProvider>
  );
};
